import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Send, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function TestEmailPage() {
  const [email, setEmail] = useState("admin@company.com");
  const { toast } = useToast();

  const testEmailMutation = useMutation({
    mutationFn: async (emailAddress: string) => {
      return await apiRequest("/api/test-email", {
        method: "POST",
        body: { email: emailAddress }
      });
    },
    onSuccess: (data) => {
      toast({
        title: "Test Email Sent!",
        description: `Email successfully sent to ${data.recipient}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Email Failed",
        description: error.message || "Failed to send test email",
        variant: "destructive",
      });
    },
  });

  const handleSendTest = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      testEmailMutation.mutate(email.trim());
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Mail className="h-6 w-6 text-purple-600" />
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          Test Email Reports
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-blue-600" />
            Send Test Security Report
          </CardTitle>
          <CardDescription>
            Send a sample security report email to test your email notification system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendTest} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address to test"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={testEmailMutation.isPending}
              className="w-full"
            >
              {testEmailMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending Test Email...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Test Email
                </>
              )}
            </Button>
          </form>

          {testEmailMutation.isSuccess && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                <CheckCircle className="h-4 w-4" />
                <p className="font-medium">Test email sent successfully!</p>
              </div>
              <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                Check your inbox for a sample security report from PCI Scan Report
              </p>
            </div>
          )}

          {testEmailMutation.isError && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                <AlertCircle className="h-4 w-4" />
                <p className="font-medium">Email test failed</p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                Check SendGrid configuration and API key
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email Testing Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-emerald-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center text-sm font-medium">1</div>
              <div>
                <p className="font-medium">Test Immediate Email</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Use the form above to send a test email right now</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-emerald-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center text-sm font-medium">2</div>
              <div>
                <p className="font-medium">Check Scheduled Scans</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Daily reports run automatically at 6:00 AM for all monitored domains</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-emerald-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center text-sm font-medium">3</div>
              <div>
                <p className="font-medium">Verify SendGrid Setup</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">Ensure SENDGRID_API_KEY is configured in environment variables</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}